import React, { useEffect, useState } from "react"
import { Group, Menu, UnstyledButton, Select } from "@mantine/core"
import { IconArrowNarrowDown, IconCaretDownFilled } from "@tabler/icons-react"
import classes from "./LanguagePicker.module.css"
import { AppEvents, AvailableLanguages, StorageKeys } from "@utils/user-config"
import { AEMImage } from "@components/AEMImage"
import secureLocalStorage from "react-secure-storage"
import { useAEMLanguageData } from "@AEM/hooks/useAEMLanguageData"
import { AEMLanguageItem } from "@AEM/models/AEMResponse.model"
import { eventTrackingItems } from "@utils/eventTracking"
import { useSetLocale } from "@hooks/useLocale"

export function useChangeLanguage() {
	const setLocale = useSetLocale()

	return React.useCallback(
		async (locale: string) => {
			await setLocale(locale)
		},
		[setLocale]
	)
}

export function LanguagePicker({
	showLabel = true,
	dataQaAttribute,
	variant = "menu",
	c = "var(--text-color-primary)",
	switchLanguage = true,
	onChange
}: {
	showLabel?: boolean
	dataQaAttribute?: string
	variant?: "menu" | "select"
	c?: string
	switchLanguage?: boolean
	onChange?: (languageCode: string) => void
}) {
	const [opened, setOpened] = useState(false)
	const [selected, setSelected] = useState<AEMLanguageItem>()
	const changeLanguage = useChangeLanguage()

	const { languageData } = useAEMLanguageData()

	const handleOnChangeLanguage = (item: AEMLanguageItem) => {
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "click",
			interactionType: "language_picker_click",
			navLinkText: item?.languageName ?? "",
			prefer_language: item?.languageName ?? ""
		})
	}

	const changeLanguageHandler = (item: AEMLanguageItem) => {
		const newLocale = item.languageCode?.toLowerCase()
		const currentLocale = secureLocalStorage.getItem(
			StorageKeys.USER_PREFERRED_LANGUAGE
		)

		setSelected(item as AEMLanguageItem)

		if (switchLanguage && newLocale && newLocale !== currentLocale) {
			changeLanguage(newLocale)
		}

		onChange?.(newLocale)
		handleOnChangeLanguage(item)
	}

	useEffect(() => {
		if (!languageData) return
		const preferredLanguage = secureLocalStorage.getItem(
			StorageKeys.USER_PREFERRED_LANGUAGE
		)
		if (preferredLanguage) {
			const languageItem = languageData?.find(
				(item: AEMLanguageItem) =>
					item.languageCode?.toLowerCase() === preferredLanguage
			)
			if (languageItem) {
				changeLanguageHandler(languageItem)
			}
		} else {
			setSelected(
				languageData?.find(
					(item: AEMLanguageItem) =>
						item.languageCode === AvailableLanguages.DEFAULT
				)
			)
		}
		if (switchLanguage) {
			window.dispatchEvent(new Event(AppEvents.USER_LANGUAGE_CHANGED))
		}
	}, [languageData])

	const items = languageData?.map((item: AEMLanguageItem, index: number) => (
		<React.Fragment key={item.languageName}>
			{index > 0 && <Menu.Divider />}
			<Menu.Item
				leftSection={
					<AEMImage
						className={classes.languageImage}
						imageData={item.languageFlag}
					/>
				}
				aria-label={
					item.languageCode === AvailableLanguages.ZH_CN ? "Chinese" : undefined
				}
				onClick={() => changeLanguageHandler(item)}
				key={item.languageName}
			>
				{item.languageName}
			</Menu.Item>
		</React.Fragment>
	))

	// Select variant - simple dropdown without flags
	if (variant === "select") {
		const selectData =
			languageData?.reduce<{ value: string; label: string }[]>((acc, item) => {
				if (!acc.some((opt) => opt.value === item.languageCode)) {
					acc.push({ value: item.languageCode, label: item.languageName })
				}
				return acc
			}, []) || []

		return (
			languageData && (
				<Select
					data={selectData}
					value={selected?.languageCode}
					styles={{
						input: {
							color: c
						}
					}}
					onChange={(value) => {
						const selectedItem = languageData.find(
							(item: AEMLanguageItem) => item.languageCode === value
						)
						if (selectedItem) {
							changeLanguageHandler(selectedItem)
						}
					}}
					rightSection={<IconCaretDownFilled size="1rem" color={c} />}
					placeholder="Select language"
					data-test-id="language-picker-select"
					data-qa={dataQaAttribute}
				/>
			)
		)
	}

	// Default menu variant - with flags
	return (
		languageData && (
			<Menu
				onOpen={() => setOpened(true)}
				onClose={() => setOpened(false)}
				radius="md"
				menuItemTabIndex={0}
			>
				<Menu.Target>
					<UnstyledButton
						className={classes.control}
						data-expanded={opened || undefined}
						data-test-id="language-picker"
						data-qa={dataQaAttribute}
						h={"100%"}
						max-height={"100%"}
						w="auto"
					>
						<Group gap="xs">
							{selected?.languageFlag && (
								<AEMImage
									className={classes.languageImage}
									imageData={selected.languageFlag}
								/>
							)}
							{showLabel && (
								<span className={classes.label}>{selected?.languageName}</span>
							)}
							<IconArrowNarrowDown
								size="1rem"
								className={classes.icon}
								stroke={1.5}
							/>
						</Group>
					</UnstyledButton>
				</Menu.Target>
				<Menu.Dropdown>{items}</Menu.Dropdown>
			</Menu>
		)
	)
}
